import { Alert, Anchor, Blockquote, Box, Button, Container, Group, Loader, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { IconBrandGoogle, IconLock } from '@tabler/icons-react';
import { Redirect, useSearch } from 'wouter';
import { BUILT_IN_FORMATS, EVENT_NAMES, type Format } from '@flow/core';
import { useAccess, useAccount } from '../account/AccountProvider';
import { paths } from '../../app/routes';
import { ShotCarousel } from './ShotCarousel';
import { Brand, Copy } from '../../ui/Brand';

/** Only same-site app paths are followed after sign-in (no open redirects). */
export const safeNext = (search: string) => {
  const next = new URLSearchParams(search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') && next !== '/' ? next : paths.dashboard;
};

const WHY: { title: string; body: string }[] = [
  {
    title: 'Keep up',
    body: "I can't keep track of everything on paper, but I type fast. {flow} lets me get the round down faster than it's delivered, so my feedback can point at exactly what was said.",
  },
  {
    title: 'Decide',
    body: 'Close rounds are hard. I follow each argument through the speeches and cross-ex, then see them all together: what was extended, dropped, turned and weighed.',
  },
  {
    title: 'Write the ballot',
    body: 'Emotions are high and the decision matters. {flow} helps me collect my thoughts, distill the round, and write feedback that follows NSDA guidelines, in my own words.',
  },
];

const FEATURES: { title: string; body: string }[] = [
  { title: 'A column per speech', body: 'In speaking order, in team colors, with marks for extended, dropped, turned and conceded. Cross-ex is one notepad per period.' },
  { title: 'Clocks that know the event', body: 'Speech and prep timers with the right times, alerts you choose, and POI windows for Parli.' },
  { title: 'Paper mode', body: 'The whole screen for the flow, with the timer in a strip. Keyboard first; nothing to click mid-speech.' },
  { title: 'Speaker notes', body: 'Quick tags and notes per debater as they speak, ready when you write speaker points.' },
  { title: 'Argument map', body: 'Where every argument went: extended, answered, weighed. AI helps build it; you check it and decide.' },
  { title: 'Ballot help', body: 'A draft from your own notes, a chat that sees your ballot, and a critique before you submit. Your words, your decision.' },
  { title: 'Offline', body: 'Everything is on your device and works with no signal. Nothing to lose when the school Wi-Fi dies.' },
  { title: 'Private', body: "Debaters' names never leave your device, and they're deleted a few days after you submit the ballot." },
];

const min = (sec: number) => (sec % 60 === 0 ? `${sec / 60}` : (sec / 60).toFixed(1));

/** One row of the formats table, straight from the built-in format definitions. */
const formatRow = (f: Format) => {
  const speeches = f.segments.filter((s) => s.kind === 'speech');
  const cx = f.segments.filter((s) => s.kind === 'questioning');
  return {
    id: f.id,
    event: f.event ? EVENT_NAMES[f.event] : f.name,
    rules: f.ruleset ?? '',
    speeches: `${speeches.length} (${min(speeches.reduce((t, s) => t + s.durationSec, 0))} min)`,
    cx: cx.length ? `${cx.length} × ${min(cx[0]!.durationSec)} min` : f.poiProtectedSec !== undefined ? 'POIs' : '—',
    prep: f.prepSec ? `${min(f.prepSec)} min` : 'none',
    sides: f.order === 'coinFlip' ? 'coin flip' : `${f.sideLabels[0]} first`,
  };
};

/** Public front page. Signed-in judges go straight to their tournaments. */
export const SplashPage = () => {
  const access = useAccess();
  const { state, signIn, signOut } = useAccount();
  const search = useSearch();
  if (access === 'allowed') return <Redirect to={safeNext(search)} replace />;

  const serverProblem = state.status === 'signedIn' && !state.denied ? state.problem : null;
  const busy = state.status === 'loading' || (access === 'checking' && !serverProblem);
  const signInButton = (size: 'sm' | 'md', testId?: string) => (
    <Button
      size={size}
      variant="filled"
      leftSection={busy ? <Loader size={14} color="white" /> : <IconBrandGoogle size={size === 'md' ? 18 : 16} />}
      disabled={state.status === 'disabled' || busy}
      onClick={signIn}
      data-testid={testId}
    >
      {busy ? 'Checking…' : 'Sign in with Google'}
    </Button>
  );

  return (
    <Box mih="100dvh" style={{ display: 'flex', flexDirection: 'column' }}>
      <Container size={1040} w="100%" py="md">
        <Group justify="space-between">
          <Group gap={10}>
            <img src="/icon.svg" alt="" width={30} height={30} />
            <Text fw={700} size="xl" style={{ letterSpacing: '-0.02em' }}>flow</Text>
          </Group>
          {!(state.status === 'signedIn' && state.denied) && signInButton('sm')}
        </Group>
      </Container>

      <Container size={1040} w="100%" pt={40} style={{ flex: 1 }}>
        <Stack gap="md" maw={680}>
          <Title order={1} style={{ fontSize: 'clamp(30px, 5vw, 44px)', lineHeight: 1.1, letterSpacing: '-0.025em' }}>
            Every round deserves a careful decision.
          </Title>
          <Text size="lg" c="dimmed">
            Debaters pour their hearts into a round, and they deserve the same effort from the judge. <Brand /> is a debate judge's notebook: flow the round, keep time, and decide with the whole debate in front of you.
          </Text>

          <Group gap={8} wrap="nowrap" align="flex-start" data-testid="invite-only">
            <IconLock size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <Text size="sm">
              <b>Invite only.</b> <Brand /> is a personal project and isn't open to new judges right now.
            </Text>
          </Group>

          {state.status === 'signedIn' && state.denied ? (
            <Alert color="yellow" title="This account isn't invited">
              <Text size="sm" mb="xs">{state.user?.email ?? 'The account you signed in with'} can't use <Brand />. Sign out, then sign in with an invited account.</Text>
              <Button size="xs" variant="default" onClick={() => void signOut()} data-testid="sign-out">Sign out</Button>
            </Alert>
          ) : (
            <Group>{signInButton('md', 'sign-in')}</Group>
          )}
          {state.status === 'unavailable' && <Text size="sm" c="dimmed">Can't reach sign-in right now. Are you online?</Text>}
          {state.status === 'disabled' && <Text size="sm" c="dimmed">Sign-in isn't set up in this build.</Text>}
          {serverProblem && <Text size="sm" c="yellow.8"><Copy>{serverProblem}</Copy></Text>}
        </Stack>

        <Box mt={48}>
          <ShotCarousel />
        </Box>

        <Box component="section" mt={80}>
          <Title order={2} size="h3" mb="lg">Why I built it</Title>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={40}>
            {WHY.map((w) => (
              <Box key={w.title}>
                <Text fw={600} mb={6}>{w.title}</Text>
                <Text size="sm" c="dimmed"><Copy>{w.body}</Copy></Text>
              </Box>
            ))}
          </SimpleGrid>
          <Blockquote mt={48} maw={760} p="lg" color="gray" cite="Richard Clayton, debate judge" styles={{ cite: { fontSize: 'var(--mantine-font-size-sm)' } }}>
            At my last tournament I judged five rounds in three events, none of which I'd ever competed in or judged. <Brand /> knew the speech order, the times and the prep for every one. No friction. I looked like a boss.
          </Blockquote>
        </Box>

        <Box component="section" mt={80}>
          <Title order={2} size="h3" mb={6}>Every event, by its own rules</Title>
          <Text size="sm" c="dimmed" mb="lg" maw={680}>Speech order, times and prep come from each league's rules. Pick the event when you add a round; the flow, clocks and ballot follow it.</Text>
          <Box style={{ overflowX: 'auto' }}>
            <Table verticalSpacing={8} horizontalSpacing="md" miw={620} data-testid="formats-table">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Event</Table.Th>
                  <Table.Th>Rules</Table.Th>
                  <Table.Th>Speeches</Table.Th>
                  <Table.Th>Cross-ex</Table.Th>
                  <Table.Th>Prep per side</Table.Th>
                  <Table.Th>Who goes first</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {BUILT_IN_FORMATS.map(formatRow).map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td fw={600}>{r.event}</Table.Td>
                    <Table.Td>{r.rules}</Table.Td>
                    <Table.Td className="tabular">{r.speeches}</Table.Td>
                    <Table.Td className="tabular">{r.cx}</Table.Td>
                    <Table.Td className="tabular">{r.prep}</Table.Td>
                    <Table.Td>{r.sides}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </Box>

        <Box component="section" mt={80} mb={80}>
          <Title order={2} size="h3" mb="lg">What's in it</Title>
          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing={32} verticalSpacing={32}>
            {FEATURES.map((f) => (
              <Box key={f.title}>
                <Text fw={600} mb={4}>{f.title}</Text>
                <Text size="sm" c="dimmed">{f.body}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      </Container>

      <Box component="footer" py="md" style={{ borderTop: '1px solid var(--grid-line)' }}>
        <Container size={1040}>
          <Group gap="lg" justify="space-between">
            <Text size="xs" c="dimmed">© {new Date().getFullYear()} Richard Clayton</Text>
            <Group gap="md">
              <Anchor size="xs" c="dimmed" href="/legal/terms">Terms</Anchor>
              <Anchor size="xs" c="dimmed" href="/legal/privacy">Privacy</Anchor>
              <Anchor size="xs" c="dimmed" href="/legal/sms">SMS alerts</Anchor>
              <Anchor size="xs" c="dimmed" href="mailto:help@judgeflow.org">help@judgeflow.org</Anchor>
            </Group>
          </Group>
        </Container>
      </Box>
    </Box>
  );
};
